import * as React from 'react';
import type {BannerType} from '@sunwebgroup/ui-components';
import {type CheckoutCarRentalService, type CheckoutCarRentalServiceOption, ServiceType} from '../../../domain/types';
import {
  SpaceBetween,
  tk,
  Typography,
  Dropdown,
  DropdownOption,
  FormFieldLabel,
  FormFieldControl,
  CheckboxElement,
  ListItem,
  IconFile,
  Button,
  Banner,
  FormField,
  FormFieldError
} from '@sunwebgroup/ui-components';
import {useService} from '@/scopes/checkout/domain/hooks';
import {sub} from 'date-fns';
import {styled} from 'styled-components';
import DateDropdown from '../../../../common/components/DateDropdown';
import {useIntl} from '@/intl';
import {editingCar, isValidForm, useCarRentalServiceStore,carCouldHaveHigherPrice} from './store';

import {addCar, editCar} from "../../../domain/actions";
import { createCarEvent, createEquipmentSelectionEvent } from '../../../domain/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

export const getOptionEquipments = (option: CheckoutCarRentalServiceOption | undefined, bookedEquipments: string[] ) => {
  if (!option) return [];
  const {equipments, maxNumberOfEquipments} = option
  const equipmentsLimit = maxNumberOfEquipments ?? equipments.length
  return equipments?.map((equipment) => ({
    ...equipment,
    selected: bookedEquipments?.includes(equipment.id),
    isElegible: bookedEquipments?.includes(equipment.id) || (bookedEquipments.length < equipmentsLimit)
  })) ?? [];
}

export const getIfCarIsSelected = (carValue: string | undefined, service: CheckoutCarRentalService) => {
  if (!service) return;
  return service.carLists?.find((car) => car.selectedValue === carValue) !== undefined;
}

export function EditFormContainerView({carValue}: { carValue: string; }) {
  const intl = useIntl();
  const {sendEvent} = useAnalytics();
  const {
    endEditingCar,
    setDriverId,
    setDriverBirthDate,
    setDriverHasCreditCard,
    toggleEquipmentEnquiry,
    setFormFieldError,
    unsetFormFieldError
  } = useCarRentalServiceStore()
  const currentEditingCar = useCarRentalServiceStore(editingCar(carValue))

  const isFormValid = useCarRentalServiceStore(isValidForm(carValue))

  const {service, setServiceValue} = useService(ServiceType.CarRental);

  const {options, availableDrivers, participants, carLists, configurations} = service || {};

  const driverMinBirthdate = sub(new Date(), {years: 99});
  const driverMaxBirthdate = sub(new Date(), {years: configurations?.minAgeDriver, days: 1});
  const carOption = options?.[carValue];
  const defaultDriverBirthdate = sub(new Date(), {years: configurations?.rentalConditionsMinAgeDriver, days: 1});
  const driverMinAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMinAgeDriver });
  const driverMaxAgeRentalConditions = sub(new Date(), { years: configurations?.rentalConditionsMaxAgeDriver });

  const {conditionsUrl} = carOption || {};
  const showWarningDriverAge = useCarRentalServiceStore(carCouldHaveHigherPrice(carValue, driverMaxAgeRentalConditions, driverMinAgeRentalConditions))
  if (!service) return;

  const handleDriverChange = (value?: string) => {
    setDriverId(carValue, value ?? null);
    setDriverBirthDate(carValue, defaultDriverBirthdate);
    setDriverHasCreditCard(carValue, false);
    unsetFormFieldError(carValue, 'driver');
  };

  const handleCreditCardChange = () => {
    setDriverHasCreditCard(carValue, !driverHasCreditCard);
    unsetFormFieldError(carValue, 'driverHasCreditCard');
    sendEvent(
      createCarEvent({
        result: !driverHasCreditCard ? 'checked' : 'unchecked',
        type: carValue,
        description: 'CreditCard clicked',
        element: 'checkbox',
      })
    )
  };

  const handleDriverBirthdateChange = (value: Date) => {
    setDriverBirthDate(carValue, value);
  };

  const {
    driverId,
    driverHasCreditCard,
    bookedEquipments,
    driverBirthDate,
    fieldsWithError,
    isEditing,
  } = currentEditingCar

  const availableEquipments = getOptionEquipments(carOption, bookedEquipments);

  const handleEquipmentsChange = (value: string) => {
    toggleEquipmentEnquiry(carValue, value);
    sendEvent(
      createEquipmentSelectionEvent({
        result: bookedEquipments.includes(value) ? 'equipment_remove' : 'equipment_add',
        type: carValue,
        description: availableEquipments.find((equipment) => equipment.id === value)?.text ?? '',
        element: 'checkbox',
        validated: true
      })
    )
  };
  const handleSaveValue =
    () => {
      if (!driverId) {
        setFormFieldError(carValue, 'driver');
        return;
      }
      if (!driverHasCreditCard) {
        setFormFieldError(carValue, 'driverHasCreditCard');
        return;
      }

      if (isFormValid) {
        if (!service) return;
        if (isEditing) {
          setServiceValue(editCar({
            value: carValue,
            participantId: driverId ?? '',
            driverBirthDate,
            bookedEquipmentIds: bookedEquipments
          })(service));
        } else {
          setServiceValue(addCar({
            value: carValue,
            participantId: driverId ?? '',
            driverBirthDate,
            bookedEquipmentIds: bookedEquipments
          })(service));
        }

        endEditingCar(carValue);

        sendEvent(
          createCarEvent({
            result: 'Car added',
            type: carValue,
            description: 'selectCar',
            element: 'button',
            validated: true
          })
        )
      }
    }

  const handleCancelButtonClick = () => {
    endEditingCar(carValue);
    sendEvent(
      createCarEvent({
        result: 'cancel modifications',
        type: carValue,
        description: 'cancel button clicked',
        element: 'button',
      })
    )
  };

  const handleConditionsLinkClick = () => {
    sendEvent(
      createCarEvent({
        result: 'conditions page opened',
        type: carValue,
        description: 'Car conditions link clicked',
        element: 'button',
      })
    )
  }

  return (
    <>
      <EditFormContainerArrow/>
      <EditFormContainer>
        <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
          <DriverFormFields>
            <CarRentalFormField $error={fieldsWithError?.driver}>
              <FormFieldLabel>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_driver_label'})}</FormFieldLabel>
              <FormFieldControl>
                <Dropdown
                  placeholder={intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_driver_placeholder'})}
                  value={driverId}
                  onValueChange={handleDriverChange}
                >
                  {availableDrivers?.map((id) => {
                    const particiantAsdriver = participants?.[id];
                    if (!particiantAsdriver) return null;
                    const {name} = particiantAsdriver;
                    return (
                      <DropdownOption key={id} value={id}
                                      disabled={Boolean(carLists?.find((car) => car.driverId === id))}>
                        {name}
                      </DropdownOption>
                    );
                  })}
                </Dropdown>
              </FormFieldControl>
              {fieldsWithError?.driver &&
                <FormFieldError>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_driver_error_message'})}</FormFieldError>}
            </CarRentalFormField>

            <CarRentalFormField>
              <FormFieldLabel>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_driver_birthdate_label'})}</FormFieldLabel>
              <FormFieldControl>
                <DriverDateDropdown
                  value={driverBirthDate}
                  onValueChange={handleDriverBirthdateChange}
                  min={driverMinBirthdate}
                  max={driverMaxBirthdate}/>
              </FormFieldControl>
            </CarRentalFormField>
          </DriverFormFields>
          <CarRentalFormField $error={fieldsWithError?.driverHasCreditCard}>
            <SpaceBetween direction="x" spacing={tk('spacing_xs')} alignItems="center">
              <CheckboxElement
                value="CreditCard"
                required
                defaultChecked={getIfCarIsSelected(carOption?.value, service) }
                checked={driverHasCreditCard}
                onCheckedChange={handleCreditCardChange}
                error={fieldsWithError?.driverHasCreditCard}/>
              <Typography $variant="body-sm-bold">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_driver_credit_card_label'})}</Typography>
            </SpaceBetween>
            {fieldsWithError?.driverHasCreditCard &&
              <FormFieldError>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_credit_card_error_message'})}</FormFieldError>}
          </CarRentalFormField>
          {showWarningDriverAge && <Banner $type={'neutral-dark' as BannerType}>
            <Typography>
              {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_driver_warning_message'}, {
                min: configurations?.rentalConditionsMinAgeDriver,
                max: configurations?.rentalConditionsMaxAgeDriver
              })}
            </Typography>
          </Banner>}
          <Typography $variant="body-xs">
            {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_advise'})}
          </Typography>
          {availableEquipments && availableEquipments.length > 0 && (
            <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
              <Typography
                $variant="label">{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_select_extras_title'})}</Typography>
              <Typography $variant="body-sm">
                {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_select_extras_description'}, {max: carOption?.maxNumberOfEquipments})}
              </Typography>
              <EquipmentsFormField>
                {availableEquipments?.map(({id, text, selected, isElegible}) => {
                    return (
                      <EquipmentField key={id} disabled={!isElegible}>
                        <ListItem.Control>
                          <CheckboxElement
                            defaultChecked={selected}
                            value={id}
                            onCheckedChange={() => handleEquipmentsChange(id)}/>
                        </ListItem.Control>
                        <ListItem.Content>
                          <ListItem.Label>{text}</ListItem.Label>
                        </ListItem.Content>
                      </EquipmentField>
                    );
                  }
                )}
              </EquipmentsFormField>
            </SpaceBetween>
          )}
          <FormActions>
            <Link
              onClick={handleConditionsLinkClick}
              href={conditionsUrl} target="_blank"
                  title={intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_car_conditions_text'})}>
              <IconFile/>
              <Typography>{intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_car_conditions_text'})}</Typography>
            </Link>
            <Button $hierarchy="tertiary" $withoutColor onClick={handleCancelButtonClick}>
              {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_car_cancel_button'})}
            </Button>
            <Button $hierarchy="secondary" disabled={!isFormValid} onClick={handleSaveValue}>
              {intl.formatMessage({id: 'checkout_steps_ancillaries_car_rental_edit_form_car_save_button'})}
            </Button>
          </FormActions>
        </SpaceBetween>
      </EditFormContainer>
    </>
  );
}

const EditFormContainerArrow = styled.div`
  background-color: ${tk('color_semantic_bg_subdued')};
  border-color: ${tk('color_semantic_bg_subdued')};
  margin-top: ${tk('spacing_sm')};
  position: relative;

  &::before, &::after {
    border: solid transparent;
    bottom: 100%;
    content: "";
    height: 0;
    left: ${tk('spacing_lg')};
    pointer-events: none;
    position: absolute;
    width: 0;
  }

  &::before {
    border-bottom-color: inherit;
  }

  &::after {
    border-bottom-color: ${tk('color_semantic_bg_subdued')};
    border-width: ${tk('spacing_sm')};
    margin-left: ${tk('spacing_2xs')};
  }
`
const EditFormContainer = styled.div`
  margin-top: ${tk('spacing_sm')};
  margin-bottom: ${tk('spacing_sm')};
  margin-left: ${tk('spacing_2xs')};
  margin-right: ${tk('spacing_2xs')};
  padding: ${tk('spacing_md')};
  background-color: ${tk('color_semantic_bg_subdued')};
  border-radius: ${tk('radius_large')};
  box-shadow: ${tk('shadow_md_bottom')};
`;
const DriverFormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk('spacing_sm')};

  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    justify-content: space-between;
  }
`;
const CarRentalFormField = styled(FormField)`
  width: 100%;
`;
const DriverDateDropdown = styled(DateDropdown)`
  ${Dropdown} {
    background-color: ${tk('color_semantic_bg')};
  }
`;
const EquipmentsFormField = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: ${tk('spacing_xs')};

  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    justify-content: space-between;
  }
`;
const EquipmentField = styled(ListItem.Root)`
  flex: 1 1 30%;
`;
const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk('spacing_sm')};

  @media ${tk('mq_desktop_sm')} {
    flex-direction: row;
    justify-content: space-between;
  }
`;
const Link = styled.a`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  text-decoration: underline;
  color: initial;
`;
